// ========================================
// SEMANTIC VALIDATOR
// Validación semántica de verbos y elementos
// Previene incoherencias como "celebra zapatos"
// ========================================

/**
 * Selecciona un verbo semánticamente compatible con un elemento
 * @param {string} contextId - ID del contexto (ej: 'cumpleanos', 'feria')
 * @param {string} element - Elemento/objeto con el que debe usarse el verbo
 * @param {Array<string>} verbPool - Lista de verbos disponibles
 * @param {Array<string>} excludeVerbs - Verbos a excluir (para evitar repetición)
 * @returns {string|null} Verbo compatible o null si no hay ninguno
 */
function selectSemanticVerb(contextId, element, verbPool, excludeVerbs = []) {
    // Verificar que SEMANTIC_MAPPINGS existe
    const mappings = (typeof window !== 'undefined' && window.SEMANTIC_MAPPINGS) 
        ? window.SEMANTIC_MAPPINGS 
        : (typeof SEMANTIC_MAPPINGS !== 'undefined' ? SEMANTIC_MAPPINGS : null);

    if (!mappings) {
        console.warn('⚠️ [SemanticValidator] SEMANTIC_MAPPINGS no disponible');
        return verbPool.find(v => !excludeVerbs.includes(v)) || null;
    }

    const semanticMap = mappings[contextId];
    if (!semanticMap) {
        console.warn(`⚠️ [SemanticValidator] No hay mapeo semántico para contexto: ${contextId}`);
        return verbPool.find(v => !excludeVerbs.includes(v)) || null;
    }

    // Normalizar elemento para comparación
    const normalizedElement = element.toLowerCase().trim();

    // Filtrar verbos compatibles con el elemento
    const compatibleVerbs = verbPool.filter(verb => {
        // Excluir verbos ya usados
        if (excludeVerbs.includes(verb)) return false;

        const validElements = semanticMap[verb];
        if (!validElements || !Array.isArray(validElements)) return false;

        // Verificar si el elemento es compatible con este verbo
        return validElements.some(validEl => {
            const normalizedValid = validEl.toLowerCase().trim();
            // Coincidencia exacta o parcial (para plurales/singulares)
            return normalizedElement.includes(normalizedValid) ||
                normalizedValid.includes(normalizedElement) ||
                normalizedElement === normalizedValid;
        });
    });

    if (compatibleVerbs.length === 0) {
        console.warn(`⚠️ [SemanticValidator] No hay verbos compatibles con "${element}" en contexto "${contextId}"`);
        console.warn(`   Verbos intentados:`, verbPool);
        return null;
    }

    // Seleccionar un verbo aleatorio de los compatibles
    const selectedVerb = compatibleVerbs[Math.floor(Math.random() * compatibleVerbs.length)];
    console.log(`✅ [SemanticValidator] Verbo seleccionado: "${selectedVerb}" para "${element}"`);

    return selectedVerb;
}

/**
 * Valida la coherencia semántica de un texto narrativo
 * @param {string} storyText - Texto de la narrativa a validar
 * @param {string} contextId - ID del contexto
 * @returns {Object} { isValid: boolean, issues: Array<string> }
 */
function validateNarrativeCoherence(storyText, contextId) {
    const issues = [];

    // Detectar patrones problemáticos conocidos
    const problematicPatterns = [
        // Verbos que no tienen sentido con objetos físicos
        { pattern: /celebra\s+\d+\s+(zapatos|libros|manzanas|pelotas|coches)/i, description: 'celebra + objeto físico' },
        { pattern: /infla\s+\d+\s+(galletas|libros|piedras|manzanas)/i, description: 'inflar + objeto no inflable' },
        { pattern: /sopla\s+\d+\s+(zapatos|libros|manzanas|pelotas)/i, description: 'soplar + objeto inadecuado' },
        { pattern: /apaga\s+\d+\s+(zapatos|libros|manzanas|pelotas)/i, description: 'apagar + objeto no apagable' },
        { pattern: /cocina\s+\d+\s+(zapatos|libros|pelotas)/i, description: 'cocinar + objeto no comestible' },
        { pattern: /come\s+\d+\s+(zapatos|libros|pelotas)/i, description: 'comer + objeto no comestible' },
        { pattern: /bebe\s+\d+\s+(zapatos|libros|pelotas|galletas)/i, description: 'beber + objeto no bebible' },
        { pattern: /lanza\s+\d+\s+(mesas|sillas|casas)/i, description: 'lanzar + objeto pesado' },
        { pattern: /vuela\s+\d+\s+(mesas|sillas|casas|zapatos)/i, description: 'volar + objeto que no vuela' },
        { pattern: /nada\s+\d+\s+(mesas|sillas|libros)/i, description: 'nadar + objeto que no nada' },

        // Verbos genéricos problemáticos que aparecen frecuentemente
        { pattern: /tiene.*celebra/i, description: 'tiene + celebra (patrón genérico)' },
        { pattern: /había.*celebra/i, description: 'había + celebra (patrón genérico)' },
    ];

    problematicPatterns.forEach(({ pattern, description }) => {
        if (pattern.test(storyText)) {
            issues.push(`Incoherencia detectada: ${description}`);
        }
    });

    // Detectar estructuras gramaticales extrañas
    const strangeStructures = [
        { pattern: /Primero hay \w+ \d+/i, description: 'Orden incorrecto: "Primero hay [nombre] [número]"' },
        { pattern: /De los \w+ \d+/i, description: 'Estructura sin sentido: "De los [nombre] [número]"' },
    ];

    strangeStructures.forEach(({ pattern, description }) => {
        if (pattern.test(storyText)) {
            issues.push(`Estructura gramatical extraña: ${description}`);
        }
    });

    // Validación de concordancia de género básica
    const genderErrors = [
        { pattern: /cuántos\s+(manzanas|peras|galletas|tortas|velas)/i, description: 'Error de género: "cuántos" + sustantivo femenino' },
        { pattern: /cuántas\s+(libros|vasos|platos|coches)/i, description: 'Error de género: "cuántas" + sustantivo masculino' },
    ];

    genderErrors.forEach(({ pattern, description }) => {
        if (pattern.test(storyText)) {
            issues.push(description);
        }
    });

    return {
        isValid: issues.length === 0,
        issues: issues
    };
}

/**
 * Obtiene un verbo de reducción/resta semánticamente válido
 * Prioriza verbos que implican reducción: usar, perder, romper, quitar, etc.
 * @param {string} contextId - ID del contexto
 * @param {string} element - Elemento con el que se usa
 * @param {Array<string>} verbPool - Lista de verbos disponibles
 * @param {Array<string>} excludeVerbs - Verbos a excluir
 * @returns {string|null} Verbo de reducción compatible
 */
function selectReductionVerb(contextId, element, verbPool, excludeVerbs = []) {
    // Verbos que implican resta/reducción
    const reductionVerbs = ['usar', 'perder', 'romper', 'quitar', 'retirar', 'gastar',
        'comer', 'llevar', 'tirar', 'vaciar', 'salir', 'irse',
        'devolver', 'llevarse', 'regalar', 'vender'];

    // Filtrar verbos de reducción que estén en el pool
    const availableReductionVerbs = verbPool.filter(v =>
        reductionVerbs.some(rv => v.includes(rv)) && !excludeVerbs.includes(v)
    );

    // Si hay verbos de reducción disponibles, seleccionar uno semánticamente válido
    if (availableReductionVerbs.length > 0) {
        const verb = selectSemanticVerb(contextId, element, availableReductionVerbs, excludeVerbs);
        if (verb) return verb;
    }

    // Fallback: intentar con todos los verbos
    return selectSemanticVerb(contextId, element, verbPool, excludeVerbs);
}

/**
 * Registra estadísticas de validación para análisis
 */
const validationStats = {
    totalValidations: 0,
    failedValidations: 0,
    issuesDetected: [],

    record(storyText, validation) {
        this.totalValidations++;
        if (!validation.isValid) {
            this.failedValidations++;
            this.issuesDetected.push({
                text: storyText.substring(0, 100) + '...',
                issues: validation.issues,
                timestamp: new Date().toISOString()
            });
        }
    },

    getReport() {
        const successRate = this.totalValidations > 0
            ? ((this.totalValidations - this.failedValidations) / this.totalValidations * 100).toFixed(2)
            : 'N/A';

        return {
            total: this.totalValidations,
            failed: this.failedValidations,
            successRate: `${successRate}%`,
            recentIssues: this.issuesDetected.slice(-10) // últimos 10
        };
    },

    reset() {
        this.totalValidations = 0;
        this.failedValidations = 0;
        this.issuesDetected = [];
    }
};

// Exportar para uso global
if (typeof window !== 'undefined') {
    window.selectSemanticVerb = selectSemanticVerb;
    window.validateNarrativeCoherence = validateNarrativeCoherence;
    window.selectReductionVerb = selectReductionVerb;
    window.validationStats = validationStats;
}

console.log('✅ [SemanticValidator] Módulo cargado');
